import { useUserVideos } from '@/hooks/useUserVideos';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Play, Video, Clock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { VideoThumbnail } from '@/components/VideoThumbnail';
import { getAnalysisIdForVideo } from '@/features/swing-lab/data/runs';

function formatDuration(seconds?: number | null): string {
  if (!seconds) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function SavedVideosList() {
  const navigate = useNavigate();
  const { rows, count, table, loading, err, loadMore, hasMore } = useUserVideos(20);
  
  const handlePlayVideo = async (video: { id: string; mux_asset_id?: string | null }) => {
    // Try to resolve video_asset_id to analysisId (runId)
    const videoAssetId = video.mux_asset_id || video.id;
    try {
      const analysisId = await getAnalysisIdForVideo(videoAssetId);
      if (analysisId) {
        // Navigate to authoritative Analysis page
        navigate(`/analysis/${analysisId}`);
      } else {
        // No analysis exists yet
        toast.info('Analysis in progress', {
          description: 'This video is being analyzed. Please check back soon.'
        });
      }
    } catch (error) {
      console.error('[SavedVideosList] Error resolving analysis:', error);
      // Fallback: navigate to swing-lab/analysis which will redirect
      navigate(`/swing-lab/analysis?id=${video.id}`);
    }
  };

  console.log('[VID] init Test Card 8');
  console.log('[VID] render', { rows: rows.length, count, table });

  if (err) {
    toast.error('Failed to load videos', { description: err });
    return (
      <Card>
        <CardHeader>
          <CardTitle>Saved Videos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-red-600">Error: {err}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Video className="h-5 w-5" />
          Saved Videos
          {count !== null && (
            <Badge variant="secondary" className="ml-auto">
              {count}
            </Badge>
          )}
        </CardTitle>
        {table && (
          <p className="text-xs text-muted-foreground">
            Loaded from: {table}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading && rows.length === 0 ? (
          <div className="space-y-3">
            <div className="text-center text-sm text-muted-foreground">
              Loading your videos...
            </div>
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-3 p-3 rounded-lg border animate-pulse">
                <div className="w-20 h-12 bg-muted rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8">
            <Video className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">No saved videos yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Upload a video to get started with swing analysis
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((video) => {
              // FIX: Use VideoThumbnail component for proper signed URL handling
              
              const playbackId = video.mux_playback_id;
              const assetId = video.mux_asset_id;
              const canPlay = playbackId || assetId;
              
              const videoTitle = video.title || `Video ${video.id.slice(0, 8)}`;
              const duration = video.duration;
              
              return (
                <div key={video.id} className="flex gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                  {/* Thumbnail */}
                  <div className="w-20 h-12 bg-muted rounded overflow-hidden flex-shrink-0">
                    {playbackId ? (
                      <VideoThumbnail
                        playbackId={playbackId}
                        playbackPolicy={video.playback_policy}
                        className="w-full h-full object-cover"
                        alt={videoTitle}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Video Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate">{videoTitle}</h4>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      {video.created_at && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(video.created_at), { addSuffix: true })}
                        </div>
                      )}
                      {duration && (
                        <div>{formatDuration(duration)}</div>
                      )}
                      {video.status && (
                        <Badge 
                          variant={video.status === 'ready' ? 'default' : 'secondary'}
                          className="text-xs px-1 py-0"
                        >
                          {video.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Play Button */}
                  <div className="flex-shrink-0">
                    {canPlay ? (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="h-8 px-2"
                        onClick={() => handlePlayVideo(video)}
                      >
                        <Play className="h-3 w-3" />
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="h-8 px-2"
                        disabled
                        title="No playback ID available"
                      >
                        <Play className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Load More Button */}
            {hasMore && (
              <div className="pt-3 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? 'Loading...' : 'Load more videos'}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}